import React from 'react';
import './ScrollStoryWidePreview.css';

export type WidePreviewKind =
  | 'instruments'
  | 'sharedPlatform'
  | 'consumables'
  | 'literature'
  | 'approval'
  | 'security';

export interface ScrollStoryWidePreviewProps {
  readonly kind: WidePreviewKind;
  readonly accent: string;
}

const ScrollStoryWidePreview: React.FC<ScrollStoryWidePreviewProps> = ({ kind, accent }) => {
  return (
    <div className="scroll-story-wide-preview story-anim" style={{ ['--preview-accent' as string]: accent }} aria-hidden>
      <div className="scroll-story-wide-preview__hud">
        <span className="scroll-story-wide-preview__hud-dot" />
        <span className="scroll-story-wide-preview__hud-dot" />
        <span className="scroll-story-wide-preview__hud-dot" />
        <span className="scroll-story-wide-preview__hud-title">LabMind</span>
      </div>
      <div className="scroll-story-wide-preview__frame">
        {kind === 'instruments' ? <PreviewInstruments /> : null}
        {kind === 'sharedPlatform' ? <PreviewSharedPlatform /> : null}
        {kind === 'consumables' ? <PreviewConsumables /> : null}
        {kind === 'literature' ? <PreviewLiterature /> : null}
        {kind === 'approval' ? <PreviewApproval /> : null}
        {kind === 'security' ? <PreviewSecurity /> : null}
      </div>
    </div>
  );
};

const PreviewInstruments: React.FC = () => (
  <>
    <div className="scroll-story-wide-preview__toolbar">
      <span className="scroll-story-wide-preview__pill">预约</span>
      <span className="scroll-story-wide-preview__pill scroll-story-wide-preview__pill--dim">设备</span>
      <span className="scroll-story-wide-preview__spacer" />
      <span className="scroll-story-wide-preview__meta">今日 · 已同步</span>
    </div>
    <table className="scroll-story-wide-preview__table">
      <thead>
        <tr>
          <th>设备</th>
          <th>状态</th>
          <th>位置</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>高分辨液质 LC-MS/MS</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--ok">空闲</span>
          </td>
          <td>A-307</td>
        </tr>
        <tr>
          <td>共聚焦显微镜</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--busy">预约中</span>
          </td>
          <td>B-118</td>
        </tr>
        <tr>
          <td>流式细胞仪</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--warn">维护</span>
          </td>
          <td>C-02</td>
        </tr>
      </tbody>
    </table>
  </>
);

const PreviewSharedPlatform: React.FC = () => (
  <>
    <div className="scroll-story-wide-preview__toolbar">
      <span className="scroll-story-wide-preview__pill">共享平台</span>
      <span className="scroll-story-wide-preview__pill scroll-story-wide-preview__pill--dim">对外开放</span>
      <span className="scroll-story-wide-preview__spacer" />
      <span className="scroll-story-wide-preview__meta">特色能力 · 双模式</span>
    </div>
    <div className="scroll-story-wide-preview__split">
      <div className="scroll-story-wide-preview__split-col">
        <div className="scroll-story-wide-preview__split-title">本团队对外</div>
        <ul className="scroll-story-wide-preview__split-list">
          <li>开放仪器 · 时段与审批</li>
          <li>预约链接 · 团队码归属</li>
          <li>接待人写入通知模板</li>
        </ul>
      </div>
      <div className="scroll-story-wide-preview__split-col">
        <div className="scroll-story-wide-preview__split-title">多团队平台</div>
        <ul className="scroll-story-wide-preview__split-list">
          <li>协约团队 · 上架审核</li>
          <li>平台命名 · 独立规则</li>
          <li>与单团队对外互斥配置</li>
        </ul>
      </div>
    </div>
    <table className="scroll-story-wide-preview__table scroll-story-wide-preview__table--tight">
      <thead>
        <tr>
          <th>对外设备</th>
          <th>模式</th>
          <th>状态</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>LC-MS/MS · 开放外部时段</td>
          <td>团队对外</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--ok">可约</span>
          </td>
        </tr>
        <tr>
          <td>电镜 · 协作网共享</td>
          <td>多团队</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--busy">协约中</span>
          </td>
        </tr>
      </tbody>
    </table>
  </>
);

const PreviewConsumables: React.FC = () => (
  <>
    <div className="scroll-story-wide-preview__toolbar">
      <span className="scroll-story-wide-preview__pill">库存</span>
      <span className="scroll-story-wide-preview__pill scroll-story-wide-preview__pill--dim">效期</span>
      <span className="scroll-story-wide-preview__spacer" />
      <span className="scroll-story-wide-preview__meta">在库物料</span>
    </div>
    <table className="scroll-story-wide-preview__table">
      <thead>
        <tr>
          <th>物料</th>
          <th>在库</th>
          <th>效期</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>PCR 预混液 2×</td>
          <td>126 盒</td>
          <td>2026-11-02</td>
        </tr>
        <tr>
          <td>细胞培养基 DMEM</td>
          <td>44 瓶</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--busy">≤30 天</span>
          </td>
        </tr>
        <tr>
          <td>Tip 10μL 无菌</td>
          <td>19 箱</td>
          <td>2027-04-18</td>
        </tr>
      </tbody>
    </table>
  </>
);

const PreviewLiterature: React.FC = () => (
  <>
    <div className="scroll-story-wide-preview__toolbar">
      <span className="scroll-story-wide-preview__pill">文献</span>
      <span className="scroll-story-wide-preview__pill scroll-story-wide-preview__pill--dim">笔记</span>
      <span className="scroll-story-wide-preview__spacer" />
      <span className="scroll-story-wide-preview__meta">团队空间</span>
    </div>
    <ul className="scroll-story-wide-preview__list">
      <li>
        <span className="scroll-story-wide-preview__list-title">CRISPR screening reveals …</span>
        <span className="scroll-story-wide-preview__list-sub">Nature Biotech · 2024 · 附件 3</span>
      </li>
      <li>
        <span className="scroll-story-wide-preview__list-title">单细胞流程 v2.1 · 实验笔记</span>
        <span className="scroll-story-wide-preview__list-sub">高亮 6 处 · 与项目「肿瘤免疫」关联</span>
      </li>
      <li>
        <span className="scroll-story-wide-preview__list-title">SOP：样品前处理</span>
        <span className="scroll-story-wide-preview__list-sub">版本 2026-Q1 · 批注已合并</span>
      </li>
    </ul>
  </>
);

const PreviewApproval: React.FC = () => (
  <>
    <div className="scroll-story-wide-preview__toolbar">
      <span className="scroll-story-wide-preview__pill">待办</span>
      <span className="scroll-story-wide-preview__pill scroll-story-wide-preview__pill--dim">已办</span>
      <span className="scroll-story-wide-preview__spacer" />
      <span className="scroll-story-wide-preview__meta">流程引擎</span>
    </div>
    <table className="scroll-story-wide-preview__table">
      <thead>
        <tr>
          <th>事项</th>
          <th>发起人</th>
          <th>状态</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>大型仪器年度维保</td>
          <td>李薇</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--busy">待审批</span>
          </td>
        </tr>
        <tr>
          <td>试剂采购 · Q2 批次</td>
          <td>周航</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--ok">已通过</span>
          </td>
        </tr>
        <tr>
          <td>访客实验室准入</td>
          <td>陈墨</td>
          <td>
            <span className="scroll-story-wide-preview__tag scroll-story-wide-preview__tag--warn">补充材料</span>
          </td>
        </tr>
      </tbody>
    </table>
  </>
);

const PreviewSecurity: React.FC = () => (
  <>
    <div className="scroll-story-wide-preview__toolbar">
      <span className="scroll-story-wide-preview__pill">部署</span>
      <span className="scroll-story-wide-preview__pill scroll-story-wide-preview__pill--dim">安全</span>
      <span className="scroll-story-wide-preview__spacer" />
      <span className="scroll-story-wide-preview__meta">策略集 · 生效中</span>
    </div>
    <div className="scroll-story-wide-preview__grid2">
      <div className="scroll-story-wide-preview__card">
        <span className="scroll-story-wide-preview__card-k">传输</span>
        <span className="scroll-story-wide-preview__card-v">TLS 1.3 · 全链路加密</span>
      </div>
      <div className="scroll-story-wide-preview__card">
        <span className="scroll-story-wide-preview__card-k">隔离</span>
        <span className="scroll-story-wide-preview__card-v">团队域 · 东院区 / 西院区</span>
      </div>
      <div className="scroll-story-wide-preview__card">
        <span className="scroll-story-wide-preview__card-k">备份</span>
        <span className="scroll-story-wide-preview__card-v">每日增量 · 跨区域副本</span>
      </div>
      <div className="scroll-story-wide-preview__card">
        <span className="scroll-story-wide-preview__card-k">审计</span>
        <span className="scroll-story-wide-preview__card-v">关键操作留痕 · 可导出</span>
      </div>
    </div>
  </>
);

export default ScrollStoryWidePreview;
